import { Fragment, useMemo, useState, type FormEvent } from 'react';
import { api, mensagemDoErro, type Disparo } from '../api';
import { useApp } from '../estado';
import { useRecurso } from '../ganchos';
import {
  formatarDataCivil,
  formatarDataHora,
  formatarMoeda,
  formatarTelefone,
  mascararDocumento,
  MOTIVOS_SUPRESSAO,
  ROTULO_RECEITA,
} from '../formatacao';
import {
  Aviso,
  Carregando,
  EstadoVazio,
  StatusBadge,
  TituloPagina,
} from '../componentes/ui';

const STATUS_FILTRO = [
  { valor: 'pendente', rotulo: 'Pendente' },
  { valor: 'na_fila', rotulo: 'Na fila' },
  { valor: 'enviado', rotulo: 'Enviado' },
  { valor: 'falhou', rotulo: 'Falhou' },
  { valor: 'suprimido', rotulo: 'Suprimido' },
  { valor: 'cancelado', rotulo: 'Cancelado' },
];

interface Filtro {
  status: string;
  receita: string;
  busca: string;
  pagina: number;
}

const FILTRO_INICIAL: Filtro = { status: '', receita: '', busca: '', pagina: 1 };

export function Disparos() {
  const { provedor, notificar } = useApp();
  const [filtro, setFiltro] = useState<Filtro>(FILTRO_INICIAL);
  const [busca, setBusca] = useState('');
  const [expandido, setExpandido] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [planejando, setPlanejando] = useState(false);

  const disparos = useRecurso(
    () =>
      api.disparos({
        status: filtro.status || undefined,
        receita: filtro.receita || undefined,
        busca: filtro.busca || undefined,
        pagina: filtro.pagina,
        porPagina: 25,
      }),
    [filtro],
  );

  const fuso = provedor?.fuso ?? 'America/Sao_Paulo';
  const dados = disparos.dados;
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / dados.porPagina)) : 1;

  const resumoFiltro = useMemo(() => {
    const partes: string[] = [];
    if (filtro.status) partes.push(`status ${filtro.status}`);
    if (filtro.receita) partes.push(ROTULO_RECEITA[filtro.receita] ?? filtro.receita);
    if (filtro.busca) partes.push(`busca “${filtro.busca}”`);
    return partes.join(' · ');
  }, [filtro]);

  const aplicarBusca = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    setFiltro((atual) => ({ ...atual, busca: busca.trim(), pagina: 1 }));
  };

  const executar = async (disparo: Disparo, acao: 'reprocessar' | 'cancelar') => {
    setOcupado(disparo.id);
    try {
      if (acao === 'reprocessar') {
        await api.reprocessarDisparo(disparo.id);
        notificar('sucesso', `Disparo de ${disparo.nome} voltou para a fila.`);
      } else {
        await api.cancelarDisparo(disparo.id);
        notificar('info', `Disparo de ${disparo.nome} cancelado.`);
      }
      await disparos.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setOcupado(null);
    }
  };

  const planejar = async () => {
    setPlanejando(true);
    try {
      const resultado = await api.planejar();
      notificar(
        resultado.avisos.length > 0 || resultado.criados === 0 ? 'info' : 'sucesso',
        [
          resultado.fonteEventos === 'elo' ? 'Fonte dos Eventos: ELO.' : '',
          resultado.criados === 0
            ? 'Nada novo para planejar hoje: a chave de cada Disparo já existe.'
            : `${resultado.criados} Disparo(s) novo(s) na fila.`,
          ...resultado.avisos,
        ].join(' '),
      );
      await disparos.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setPlanejando(false);
    }
  };

  return (
    <>
      <TituloPagina
        titulo="Disparos"
        descricao="A fila do Provedor, do planejamento ao recibo do canal. Cada linha é uma mensagem única — a chave natural impede duplicidade."
        acoes={
          <button
            type="button"
            className="botao botao--primario"
            onClick={() => void planejar()}
            disabled={planejando}
          >
            {planejando ? 'Planejando…' : 'Planejar o dia'}
          </button>
        }
      />

      <section className="cartao cartao--compacto" aria-label="Filtros">
        <form className="filtros" onSubmit={aplicarBusca}>
          <div className="campo">
            <label className="campo__rotulo" htmlFor="filtro-status">
              Status
            </label>
            <select
              id="filtro-status"
              className="entrada"
              value={filtro.status}
              onChange={(evento) =>
                setFiltro((atual) => ({ ...atual, status: evento.target.value, pagina: 1 }))
              }
            >
              <option value="">Todos</option>
              {STATUS_FILTRO.map((status) => (
                <option key={status.valor} value={status.valor}>
                  {status.rotulo}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label className="campo__rotulo" htmlFor="filtro-receita">
              Receita
            </label>
            <select
              id="filtro-receita"
              className="entrada"
              value={filtro.receita}
              onChange={(evento) =>
                setFiltro((atual) => ({ ...atual, receita: evento.target.value, pagina: 1 }))
              }
            >
              <option value="">Todas</option>
              {Object.entries(ROTULO_RECEITA).map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </select>
          </div>

          <div className="campo" style={{ flex: '1 1 16rem' }}>
            <label className="campo__rotulo" htmlFor="filtro-busca">
              Buscar Titular, documento ou fatura
            </label>
            <input
              id="filtro-busca"
              className="entrada"
              type="search"
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Ex.: Ana, 12345678900, 884512"
            />
          </div>

          <div className="linha">
            <button type="submit" className="botao botao--secundario">
              Filtrar
            </button>
            <button
              type="button"
              className="botao botao--fantasma"
              onClick={() => {
                setBusca('');
                setFiltro(FILTRO_INICIAL);
              }}
            >
              Limpar
            </button>
          </div>
        </form>
      </section>

      {disparos.carregando && !dados ? (
        <Carregando texto="Consultando a fila…" />
      ) : disparos.erro && !dados ? (
        <Aviso tipo="erro" titulo="Não foi possível ler a fila">
          {disparos.erro}
        </Aviso>
      ) : !dados || dados.itens.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum Disparo encontrado"
          texto={
            resumoFiltro
              ? `Nada corresponde a ${resumoFiltro}. Limpe os filtros ou planeje o dia.`
              : 'A fila está vazia. Ative uma receita e toque em “Planejar o dia”.'
          }
          icone="◷"
        />
      ) : (
        <>
          <div className="linha linha--separada">
            <span className="texto-pequeno" role="status">
              {dados.total} Disparo(s){resumoFiltro ? ` · ${resumoFiltro}` : ''}
            </span>
            <span className="texto-pequeno">
              Página {dados.pagina} de {totalPaginas}
            </span>
          </div>

          <div className="tabela-rolagem">
            <table>
              <caption className="sr-apenas">Disparos do Provedor</caption>
              <thead>
                <tr>
                  <th scope="col">Titular</th>
                  <th scope="col">Receita</th>
                  <th scope="col">Agendado para</th>
                  <th scope="col">Valor</th>
                  <th scope="col">Status</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((disparo) => {
                  const aberto = expandido === disparo.id;
                  const podeReprocessar = ['falhou', 'suprimido', 'cancelado'].includes(
                    disparo.status,
                  );
                  const podeCancelar = ['pendente', 'na_fila', 'falhou'].includes(disparo.status);
                  return (
                    <Fragment key={disparo.id}>
                      <tr>
                        <td>
                          <strong>{disparo.nome}</strong>
                          <br />
                          <span className="texto-pequeno">
                            {mascararDocumento(disparo.documento)} ·{' '}
                            {formatarTelefone(disparo.telefone)}
                          </span>
                        </td>
                        <td>
                          {ROTULO_RECEITA[disparo.receita] ?? disparo.receita}
                          {disparo.acaoErp ? (
                            <span className="badge badge--alerta" style={{ marginLeft: '0.35rem' }}>
                              <span aria-hidden="true">⚠</span> ERP
                            </span>
                          ) : null}
                        </td>
                        <td className="numero">{formatarDataHora(disparo.agendadoPara, fuso)}</td>
                        <td className="numero">{formatarMoeda(disparo.valorCentavos)}</td>
                        <td>
                          <StatusBadge status={disparo.status} />
                        </td>
                        <td>
                          <div className="acoes-tabela">
                            <button
                              type="button"
                              className="botao botao--secundario botao--pequeno"
                              aria-expanded={aberto}
                              aria-controls={`detalhe-${disparo.id}`}
                              onClick={() => setExpandido(aberto ? null : disparo.id)}
                            >
                              {aberto ? 'Ocultar' : 'Detalhes'}
                            </button>
                            {podeReprocessar ? (
                              <button
                                type="button"
                                className="botao botao--secundario botao--pequeno"
                                onClick={() => void executar(disparo, 'reprocessar')}
                                disabled={ocupado === disparo.id}
                              >
                                Reprocessar
                              </button>
                            ) : null}
                            {podeCancelar ? (
                              <button
                                type="button"
                                className="botao botao--perigo botao--pequeno"
                                onClick={() => void executar(disparo, 'cancelar')}
                                disabled={ocupado === disparo.id}
                              >
                                Cancelar
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {aberto ? (
                        <tr key={`${disparo.id}-detalhe`} id={`detalhe-${disparo.id}`}>
                          <td colSpan={6}>
                            <div className="detalhe">
                              <dl>
                                <div>
                                  <dt>Chave de deduplicação</dt>
                                  <dd className="mono">{disparo.chave}</dd>
                                </div>
                                <div>
                                  <dt>Contrato / Fatura</dt>
                                  <dd>
                                    {disparo.contrato || '—'} · {disparo.fatura ?? '—'}
                                  </dd>
                                </div>
                                <div>
                                  <dt>Vencimento</dt>
                                  <dd>{formatarDataCivil(disparo.vencimento)}</dd>
                                </div>
                                <div>
                                  <dt>Tentativas</dt>
                                  <dd>{disparo.tentativas}</dd>
                                </div>
                                <div>
                                  <dt>Recibo do canal</dt>
                                  <dd className="mono">{disparo.recibo ?? '—'}</dd>
                                </div>
                                <div>
                                  <dt>Situação no canal</dt>
                                  <dd>
                                    {disparo.situacaoCanal ? (
                                      <StatusBadge status={disparo.situacaoCanal} />
                                    ) : (
                                      '—'
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt>Enviado em</dt>
                                  <dd>{formatarDataHora(disparo.enviadoEm, fuso)}</dd>
                                </div>
                                <div>
                                  <dt>Ação no ERP</dt>
                                  <dd>
                                    {disparo.acaoErp
                                      ? `Desbloqueio por ${disparo.acaoErp.dias} dia(s)`
                                      : 'Nenhuma'}
                                  </dd>
                                </div>
                              </dl>

                              {disparo.ultimoErro ? (
                                <div style={{ marginTop: '0.85rem' }}>
                                  <Aviso tipo="erro" titulo="Último erro">
                                    {disparo.ultimoErro}
                                  </Aviso>
                                </div>
                              ) : null}

                              {disparo.motivoSupressao ? (
                                <div style={{ marginTop: '0.85rem' }}>
                                  <Aviso tipo="alerta" titulo="Motivo da supressão">
                                    {MOTIVOS_SUPRESSAO[disparo.motivoSupressao] ??
                                      disparo.motivoSupressao}
                                  </Aviso>
                                </div>
                              ) : null}

                              <div style={{ marginTop: '0.85rem' }}>
                                <dt className="texto-pequeno">Variáveis do envio</dt>
                                <dd className="mono">
                                  {Object.entries(disparo.variaveis).length === 0
                                    ? '—'
                                    : Object.entries(disparo.variaveis)
                                        .map(([chave, valor]) => `${chave}=${valor}`)
                                        .join(' · ')}
                                </dd>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <nav className="linha linha--separada" aria-label="Paginação">
            <button
              type="button"
              className="botao botao--secundario botao--pequeno"
              onClick={() =>
                setFiltro((atual) => ({ ...atual, pagina: Math.max(1, atual.pagina - 1) }))
              }
              disabled={dados.pagina <= 1}
            >
              ← Anterior
            </button>
            <span className="texto-pequeno">
              {dados.itens.length} de {dados.total} nesta página
            </span>
            <button
              type="button"
              className="botao botao--secundario botao--pequeno"
              onClick={() =>
                setFiltro((atual) => ({
                  ...atual,
                  pagina: Math.min(totalPaginas, atual.pagina + 1),
                }))
              }
              disabled={dados.pagina >= totalPaginas}
            >
              Próxima →
            </button>
          </nav>
        </>
      )}
    </>
  );
}
